import { problemResponse, registry, undeclaredQueryRefused } from "../registry.js";
import { isoDateTime, z } from "../zod.js";
import { Uuid } from "../identity/admin-schemas.js";
import { LifecycleEventItem } from "./schemas.js";

// Spec 15 §4 S3, the fleet overview (M15 shell lane, D-625). One read that
// carries the KPI tiles, the "needs attention" rows and the fleet's newest
// lifecycle changes. Tiles and attention rows come from server-side SOURCES
// (apps/api/src/modules/platform-console/fleet/fleet-source.ts); a tile whose
// store does not exist yet has no source and is absent from `tiles`, never
// present as zero. `key` and `kind` are open strings so that a later source
// adds a tile without an OpenAPI change.

const Key = z
  .string()
  .regex(/^[a-z][a-z0-9_]*$/u)
  .max(64);

export const PlatformFleetTile = z
  .object({
    key: Key,
    value: z.number().int().min(0),
    tone: z.enum(["neutral", "ok", "warn", "bad"]),
  })
  .openapi("PlatformFleetTile");

export const PlatformFleetTenantRef = z
  .object({
    id: Uuid,
    name: z.string(),
    subdomain: z.string(),
    status: z.string(),
    sandbox: z.boolean(),
  })
  .openapi("PlatformFleetTenantRef");

export const PlatformFleetAttentionItem = z
  .object({
    kind: Key,
    severity: z.enum(["bad", "warn"]),
    /** When the tenant entered the state, when a lifecycle row records it. */
    since: isoDateTime().nullable(),
    tenant: PlatformFleetTenantRef,
  })
  .openapi("PlatformFleetAttentionItem");

export const PlatformFleetEvent = LifecycleEventItem.extend({
  /** The tenant the row moved. A tenant is never hard-deleted (a purge keeps
   * its tombstone, spec 16 §2.1), so every row names one. */
  tenant: PlatformFleetTenantRef,
}).openapi("PlatformFleetEvent");

export const PlatformFleetOverviewResponse = z
  .object({
    generated_at: isoDateTime(),
    tiles: z.array(PlatformFleetTile),
    /** Worst first, then longest-standing; at most 25 rows. */
    attention: z.array(PlatformFleetAttentionItem),
    /** Every attention row the sources reported, shown or not. */
    attention_total: z.number().int().min(0),
    /** The fleet's ten newest lifecycle rows. */
    recent_events: z.array(PlatformFleetEvent),
    /** Sources that failed to answer; their tiles and rows are missing. */
    unavailable: z.array(Key),
  })
  .openapi("PlatformFleetOverviewResponse");

registry.registerPath({
  method: "get",
  path: "/v1/platform/fleet/overview",
  operationId: "platformFleetOverview",
  tags: ["platform"],
  summary: "The fleet overview: KPI tiles, tenants needing attention, recent changes (all staff)",
  description:
    "Spec 15 §4 S3 (D-625). Tenant counts by status come from module 16 through TenancyService; the staff-signed-in tile is returned only to the roles that may read the staff list (platform_admin, platform_readonly). A source that fails is named in `unavailable` and the rest of the page still answers.",
  security: [{ platformSessionCookie: [] }],
  responses: {
    200: {
      description: "The overview",
      content: { "application/json": { schema: PlatformFleetOverviewResponse } },
    },
    400: undeclaredQueryRefused,
    401: problemResponse("Not authenticated"),
    403: problemResponse(
      "Not permitted, or TOTP_ENROLLMENT_REQUIRED while the caller's TOTP is unconfirmed",
    ),
    404: problemResponse("Requested on a host other than the admin host"),
  },
});
